/**
 * ReadmodelSourceSection — the inspector card pane's READMODEL configuration
 * (TV1-2291 · S26).
 *
 * The card pane is SOURCE-AWARE: `widgetDataSourceOf( type )` (the ticket-18
 * catalog, never the type's spelling) decides which sections it mounts. An
 * `analytics` card gets the seven verbatim `WidgetBuilder/Sections/*`; a `static`
 * card gets its content; a `readmodel` card gets THIS — collection, then
 * field / operator / value, plus the row cap, plus the two fields `alert` alone
 * declares.
 *
 * ## Why this is not a `WidgetBuilder/Sections/*` component
 *
 * Those seven are Cube-shaped by construction: `SectionProps` hands them
 * `getAvailableFields` / `getAvailableMeasures` resolved off the live Cube `/meta`
 * catalog, and `FiltersSection` speaks the Cube filter grammar
 * (`{ member, operator, values }`, ten builder operators). A readmodel query has
 * no cube, no measures, and a DIFFERENT filter grammar (`{ field, op, value }`,
 * sixteen operators from `pv3-types`). Reusing `FiltersSection` here would mean
 * translating between the two grammars in the editor — the exact silent
 * mistranslation `isReadmodelFilter` exists to refuse at the render seam and
 * `ReadmodelFilterSchema` refuses at rest.
 *
 * ## Every mutation goes through `applyUpdate`, never `updateConfig`
 *
 * The panel is CONTROLLED — `selectedWidget` is a prop — so two edits dispatched
 * in the same tick would both read the pre-edit value and the first would be lost.
 * `WidgetInspector` solves that with `latestRef`: `applyUpdate` composes a
 * FUNCTIONAL patch against the most recently EMITTED definition. Changing the
 * collection is exactly the shape that bug bites (it must rewrite `collection` AND
 * repoint every filter clause), so it is ONE `applyUpdate` patch — the same call
 * shape `BasicSection`'s cube change uses to reset measures/dimensions/filters/
 * orderBy/dateRange in one go. Never two `updateConfig` calls.
 */

import { ActionIcon, Button, Group, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { componentsTestIds } from '@components/testids';
import {
  ALERT_SEVERITIES,
  READMODEL_CATALOG,
  READMODEL_COLLECTION_IDS,
  READMODEL_FILTER_OPS,
  SEVERITY_WIDGET_TYPES,
  VALUELESS_READMODEL_OPS,
  getReadmodelCollection,
  type AlertSeverity,
  type ReadmodelCollectionMeta,
  type ReadmodelFilter,
  type ReadmodelFilterOp
} from '../Widgets/readmodelSource';
import { CAPTION_WIDGET_TYPES, LINKABLE_WIDGET_TYPES } from '../Widgets/readmodelSource';
import { validateWebWidget } from '../Widgets/WebWidget/WebWidgetValidators';
import type { ValidationErrors } from '../Widgets/validateWidget';
import type { WidgetDefinition } from '../Widgets/WidgetBuilder/types';
import { ColorStepsEditor } from './ColorStepsEditor';
import classes from './WidgetInspector.module.css';

/**
 * TV1-2291 (t25) — `SEVERITY_WIDGET_TYPES` and `ALERT_SEVERITIES` MOVED to
 * `Widgets/readmodelSource.ts`, the pure grammar module, now that three surfaces
 * need them (this pane, `AlertWidget`, and `validateAlert`). Re-exported here so
 * ticket 26's import sites keep working; there is ONE definition, and it is not
 * this one.
 */
export { ALERT_SEVERITIES, SEVERITY_WIDGET_TYPES };
export type { AlertSeverity };

/**
 * Operators whose operand is a LIST (`filterOpMap` reads `expected` through
 * `asArray`, so a bare string can never match). The inspector edits them as a
 * comma-separated field and stores a real array — a scalar here is the
 * silently-empty-widget failure mode, not a type error.
 */
const ARRAY_OPERAND_OPS: ReadonlySet<ReadmodelFilterOp> = new Set<ReadmodelFilterOp>([
  'in', 'nin', 'some', 'none', 'every'
]);

/** Spanish fallbacks for the 16 operators. Keys are `widget_inspector_readmodel_op_*`. */
const OPERATOR_LABEL: Record<ReadmodelFilterOp, string> = {
  eq: 'es igual a',
  neq: 'no es igual a',
  lt: 'es menor que',
  gt: 'es mayor que',
  lte: 'es menor o igual que',
  gte: 'es mayor o igual que',
  in: 'está en la lista',
  nin: 'no está en la lista',
  contains: 'contiene',
  notContains: 'no contiene',
  some: 'tiene alguno de',
  none: 'no tiene ninguno de',
  every: 'tiene solo',
  exists: 'tiene valor',
  notExists: 'no tiene valor',
  notEmpty: 'no está vacío'
};

const SEVERITY_LABEL: Record<AlertSeverity, string> = {
  low: 'Baja (informativa)',
  medium: 'Media (atención)',
  high: 'Alta (urgente)'
};

// =============================================================================
// Value <-> text
// =============================================================================

/** Render a stored operand in the text input. An array shows as a comma list. */
const valueToText = ( value: unknown ): string => {
  if ( value === undefined || value === null ) return '';
  if ( Array.isArray( value )) return value.map( String ).join( ', ' );
  return String( value );
};

/** Parse the text input back, honouring the operator's operand ARITY. */
const textToValue = ( text: string, op: ReadmodelFilterOp ): unknown => {
  if ( !ARRAY_OPERAND_OPS.has( op )) return text;
  return text
    .split( ',' )
    .map(( entry ) => entry.trim())
    .filter(( entry ) => entry.length > 0 );
};

/**
 * Drop the operand when the operator does not take one.
 *
 * `exists` / `notExists` / `notEmpty` are unary predicates over the row value
 * alone (`coerceFilterType` returns `undefined` for them server-side), which is
 * why `ReadmodelFilter.value` is optional rather than `unknown`-and-required.
 * Leaving a stale operand behind would persist a field the schema does not expect
 * on that clause.
 */
const withOperator = ( clause: ReadmodelFilter, op: ReadmodelFilterOp ): ReadmodelFilter => {
  if ( VALUELESS_READMODEL_OPS.has( op )) {
    const { value: _dropped, ...rest } = clause;
    return { ...rest, op };
  }
  return { ...clause, op, value: textToValue( valueToText( clause.value ), op ) };
};

// =============================================================================
// Collection change — the stale-field reset
// =============================================================================

/**
 * Repoint every clause at a field the NEW collection actually has.
 *
 * A stale field is the whole defect: `crm_questions_readmodel.answered` carried
 * over to `items_publishing_size_guide_readmodel` reads a key that is not there,
 * so the predicate is false for every row and the widget renders a confident `0`
 * with no error anywhere. `readmodelSource.readField` returns `undefined` for a
 * missing path and `matchesClause` compares against it happily — nothing throws.
 *
 * Reset (rather than drop) so the clause the user built stays visible and
 * editable; only its target moves to a valid member.
 */
const repointFilterToCollection = (
  filter: ReadmodelFilter[] | undefined,
  collection: ReadmodelCollectionMeta | undefined
): ReadmodelFilter[] => {
  const clauses = filter ?? [];
  if ( !collection ) return clauses;
  const fallbackField = collection.fields[0];
  if ( fallbackField === undefined ) return [];
  return clauses.map(( clause ) => (
    collection.fields.includes( clause.field ) ? clause : { ...clause, field: fallbackField }
  ));
};

// =============================================================================
// Validation
// =============================================================================

/**
 * Validate a readmodel card — now a straight DELEGATION to the real validators.
 *
 * ## What this was, and why it is not that any more
 *
 * Ticket 26 shipped this as a LOCAL STAND-IN, and said so: the front-end
 * `widgetValidators` map is an exhaustive `Record<WidgetType, …>`, and `count` /
 * `alert` / `list` / `progress` were not members of the `WidgetType` union yet, so
 * `validateWebWidget` answered `Invalid widget type: alert` for every readmodel
 * card. A stand-in was the only way to validate the binding at all.
 *
 * Ticket 25 widened the union and added the real per-type validators
 * (`WebWidgetValidators.validateCount` / `validateAlert` / `validateList` /
 * `validateProgress`), so the stand-in is retired: this delegates, and the binding
 * rules it used to state inline now live in `validateReadmodelBinding` beside every
 * other widget's rules. Two validators disagreeing about whether a card is complete
 * — one used by the inspector, one by the save path — is exactly the drift the
 * exhaustive map exists to prevent.
 *
 * Kept as a named export rather than replaced at the call site: `WidgetInspector`
 * reads it as "the readmodel branch of the validation seam", and that stays true.
 *
 * ⚠️ It is `validateWebWidget`, not `validateWidget( …, device, … )`: the four
 * readmodel types are WEB-ONLY (`webWidgetTypes`), and the inspector is a desktop
 * surface. `widgetValidatorsMobile` carries entries for them only because that map
 * is total.
 */
export const validateReadmodelWidget = (
  definition: WidgetDefinition,
  t: TFunction
): ValidationErrors | null => validateWebWidget( definition, t );

// =============================================================================
// Component
// =============================================================================

export type ReadmodelSourceSectionProps = {
  /** The selected card. Read-only — every edit is emitted through `applyUpdate`. */
  definition: WidgetDefinition;
  /**
   * The panel's FUNCTIONAL update seam. Composes same-tick edits through
   * `WidgetInspector`'s `latestRef`; see this file's header for why nothing here
   * may use the per-key `updateConfig` instead.
   */
  applyUpdate: ( update: ( current: WidgetDefinition ) => WidgetDefinition ) => void;
  validationErrors?: ValidationErrors | null;
};

export default function ReadmodelSourceSection({
  definition,
  applyUpdate,
  validationErrors
}: ReadmodelSourceSectionProps ) {
  const { t } = useTranslation( 'widgets' );

  const sectionErrors = validationErrors?.readmodelSource;
  const collection = definition.collection
    ? getReadmodelCollection( definition.collection )
    : undefined;
  const clauses = definition.filter ?? [];

  const collectionOptions = READMODEL_COLLECTION_IDS.map(( id ) => ({
    value: id,
    label: READMODEL_CATALOG[id].label
  }));

  const fieldOptions = ( collection?.fields ?? []).map(( field ) => ({
    value: field,
    label: field
  }));

  const operatorOptions = READMODEL_FILTER_OPS.map(( op ) => ({
    value: op,
    label: t( `widget_inspector_readmodel_op_${ op }`, OPERATOR_LABEL[op])
  }));

  /**
   * ONE patch: the collection AND the repointed clauses. Two `updateConfig` calls
   * in this handler would be the ticket-16 lost update — the second reads the
   * pre-edit definition and the first is discarded.
   */
  const handleCollectionChange = ( value: string | null ) => {
    const nextId = value ?? '';
    const nextCollection = getReadmodelCollection( nextId );
    applyUpdate(( current ) => ({
      ...current,
      collection: nextId,
      filter: repointFilterToCollection( current.filter, nextCollection )
    }));
  };

  const patchClause = ( index: number, next: ReadmodelFilter ) => {
    applyUpdate(( current ) => ({
      ...current,
      filter: ( current.filter ?? []).map(( clause, i ) => ( i === index ? next : clause ))
    }));
  };

  const addClause = () => {
    const field = collection?.fields[0];
    if ( field === undefined ) return;
    applyUpdate(( current ) => ({
      ...current,
      filter: [ ...( current.filter ?? []), { field, op: 'eq' as ReadmodelFilterOp, value: '' }]
    }));
  };

  const removeClause = ( index: number ) => {
    applyUpdate(( current ) => ({
      ...current,
      filter: ( current.filter ?? []).filter(( _clause, i ) => i !== index )
    }));
  };

  const updateComponentProp = ( key: string, value: string | undefined ) => {
    applyUpdate(( current ) => {
      const props = { ...( current.widgetComponentProps ?? {}) };
      if ( value === undefined ) delete props[key];
      else props[key] = value;
      return { ...current, widgetComponentProps: props };
    });
  };

  return (
    <Stack gap="sm">
      <div>
        <Text className={classes.fieldLabel}>
          {t( 'widget_inspector_readmodel_collection_label', 'Colección' )}
        </Text>
        <Select
          size="xs"
          data-testid={componentsTestIds.reportsInspectorCollection}
          data={collectionOptions}
          value={definition.collection ?? null}
          placeholder={t( 'widget_inspector_readmodel_collection_placeholder', 'Elegí una colección' )}
          allowDeselect={false}
          error={sectionErrors?.collection}
          onChange={handleCollectionChange}
        />
        {collection ? (
          <Text className={classes.hint} mt={5}>{collection.description}</Text>
        ) : null}
      </div>

      {/*
        The notice is about what the ROUTE can do, not about what it accepts. A
        base route does NOT reject a `filter` — pv3 defaults every query route to
        `defaultQuerySchemas.query` and `defaultQueryHandler` applies it (the SDD's
        original claim to the contrary was disproved during ticket 22). What is
        true is that it answers with a bare array carrying no `count`, and that it
        drops `offset` on the floor, so the filter and the row cap are resolved
        here instead — and they move TOGETHER, because a server `limit` applied
        before a client-side predicate answers "how many of the first N match"
        rather than "how many match".
      */}
      {collection && collection.searchOperation === null ? (
        <Text
          className={classes.note}
          data-testid={componentsTestIds.reportsInspectorClientFilterNotice}
        >
          {t(
            'widget_inspector_readmodel_client_filter_notice',
            'Esta colección no tiene ruta de búsqueda: la ruta base devuelve las filas sin un total y descarta el desplazamiento, así que el filtro y el límite se aplican en el navegador sobre todo lo que devuelve.'
          )}
        </Text>
      ) : null}

      <div>
        <Text className={classes.fieldLabel}>
          {t( 'widget_inspector_readmodel_filter_label', 'Condiciones' )}
        </Text>

        {!collection ? (
          <Text className={classes.hint}>
            {t(
              'widget_inspector_readmodel_filter_needs_collection',
              'Elegí una colección para ver sus campos filtrables.'
            )}
          </Text>
        ) : clauses.length === 0 ? (
          <Text className={classes.hint}>
            {t(
              'widget_inspector_readmodel_filter_empty',
              'Sin condiciones: la tarjeta mira todas las filas de la colección.'
            )}
          </Text>
        ) : (
          <Stack gap="xs">
            {clauses.map(( clause, index ) => (
              <div key={index} className={classes.filterClause}>
                <Select
                  size="xs"
                  data-testid={componentsTestIds.reportsInspectorFilterField( index )}
                  aria-label={t( 'widget_inspector_readmodel_field_label', 'Campo' )}
                  data={fieldOptions}
                  value={clause.field}
                  allowDeselect={false}
                  onChange={( value ) => patchClause( index, { ...clause, field: value ?? clause.field })}
                />
                <Group gap="xxs" wrap="nowrap" align="flex-start">
                  <Select
                    size="xs"
                    className={classes.filterGrow}
                    data-testid={componentsTestIds.reportsInspectorFilterOp( index )}
                    aria-label={t( 'widget_inspector_readmodel_operator_label', 'Operador' )}
                    data={operatorOptions}
                    value={clause.op}
                    allowDeselect={false}
                    onChange={( value ) =>
                      patchClause( index, withOperator( clause, ( value ?? clause.op ) as ReadmodelFilterOp ))
                    }
                  />
                  <ActionIcon
                    variant="transparent"
                    color="gray"
                    size="sm"
                    radius="xs"
                    mt={2}
                    data-testid={componentsTestIds.reportsInspectorFilterRemove( index )}
                    aria-label={t( 'widget_inspector_readmodel_filter_remove', 'Quitar condición' )}
                    onClick={() => removeClause( index )}
                  >
                    <IconTrash size={15} className={classes.filterRemoveIcon} />
                  </ActionIcon>
                </Group>
                {VALUELESS_READMODEL_OPS.has( clause.op ) ? null : (
                  <TextInput
                    size="xs"
                    data-testid={componentsTestIds.reportsInspectorFilterValue( index )}
                    aria-label={t( 'widget_inspector_readmodel_value_label', 'Valor' )}
                    value={valueToText( clause.value )}
                    placeholder={
                      ARRAY_OPERAND_OPS.has( clause.op )
                        ? t( 'widget_inspector_readmodel_value_list_placeholder', 'Valores separados por coma' )
                        : t( 'widget_inspector_readmodel_value_placeholder', 'Valor' )
                    }
                    onChange={( event ) =>
                      patchClause( index, {
                        ...clause,
                        value: textToValue( event.currentTarget.value, clause.op )
                      })
                    }
                  />
                )}
              </div>
            ))}
          </Stack>
        )}

        {collection ? (
          <Button
            size="xs"
            radius="xs"
            variant="light"
            mt="xs"
            className={classes.cancelButton}
            leftSection={<IconPlus size={14} />}
            data-testid={componentsTestIds.reportsInspectorFilterAdd}
            onClick={addClause}
          >
            {t( 'widget_inspector_readmodel_filter_add', 'Agregar condición' )}
          </Button>
        ) : null}
      </div>

      <div>
        <Text className={classes.fieldLabel}>
          {t( 'widget_inspector_readmodel_limit_label', 'Máximo de filas' )}
        </Text>
        <NumberInput
          size="xs"
          min={1}
          allowDecimal={false}
          data-testid={componentsTestIds.reportsInspectorLimit}
          value={definition.limit ?? ''}
          placeholder={t( 'widget_inspector_readmodel_limit_placeholder', 'Sin máximo' )}
          onChange={( value ) =>
            applyUpdate(( current ) => ({
              ...current,
              limit: typeof value === 'number' && value > 0 ? value : undefined
            }))
          }
        />
        <Text className={classes.hint} mt={5}>
          {t(
            'widget_inspector_readmodel_limit_hint',
            'El máximo viaja junto con el filtro: si el filtro se resuelve en el navegador, el recorte también.'
          )}
        </Text>
      </div>

      {/*
        `severity` and `href` ride inside `widgetComponentProps`, NOT at the top
        level of the definition — mirroring `kpi.href`, the only href in the app
        today. Ticket 23's schema note is explicit about why: a top-level `href`
        would be inherited by the model-facing definition schema the day someone
        adds `alert` to `MODEL_WIDGET_VARIANTS`, handing a model a raw navigation
        target. The renderer (ticket 25) MUST route this through `sanitizeKpiHref`
        and fall back to plain text — the schema's `S.string()` is not a control.
      */}
      {SEVERITY_WIDGET_TYPES.has( definition.type ) ? (
        <>
          <div>
            <Text className={classes.fieldLabel}>
              {t( 'widget_inspector_readmodel_severity_label', 'Severidad' )}
            </Text>
            <Select
              size="xs"
              data-testid={componentsTestIds.reportsInspectorSeverity}
              data={ALERT_SEVERITIES.map(( severity ) => ({
                value: severity,
                label: t( `widget_inspector_readmodel_severity_${ severity }`, SEVERITY_LABEL[severity])
              }))}
              value={( definition.widgetComponentProps?.severity as string ) ?? null}
              placeholder={t( 'widget_inspector_readmodel_severity_placeholder', 'Elegí una severidad' )}
              allowDeselect={false}
              error={sectionErrors?.severity}
              onChange={( value ) => updateComponentProp( 'severity', value ?? undefined )}
            />
          </div>

          {/*
            STEP COLOURS (2026-08-18), directly under the severity they override.
            Placement is the explanation: the two controls answer the same question
            at different resolutions — what colour is this tile — and a reader of the
            panel needs to see that the lower one wins where it applies.

            Written through `applyUpdate` rather than `updateComponentProp`, which
            takes `string | undefined`. This value is an ARRAY, and widening that
            helper's signature would let any caller put a non-string on a prop bag
            that mostly holds strings.
          */}
          <div>
            <Text className={classes.fieldLabel}>
              {t( 'widget_inspector_color_steps_label', 'Colores por tramo (opcional)' )}
            </Text>
            <ColorStepsEditor
              value={definition.widgetComponentProps?.colorSteps}
              onChange={( steps ) => applyUpdate(( current ) => {
                const props = { ...( current.widgetComponentProps ?? {}) };
                if ( steps === undefined ) delete props.colorSteps;
                else props.colorSteps = steps;
                return { ...current, widgetComponentProps: props };
              })}
            />
          </div>

        </>
      ) : null}

      {/*
        ── CAPTION AND LINK, for every readmodel tile that can render them ──────
        (2026-08-19)

        ⚠️ These sit OUTSIDE the `SEVERITY_WIDGET_TYPES` branch above, and that
        placement is the fix, not a tidy-up.

        The href used to be inside it, so only `alert` had one — review item 4 asked
        for `count` to be linkable too, and there was no reason beyond where the JSX
        happened to sit. The caption had NO editor at all, which is what turned the
        presets' seed values into the permanent "always says Publicaciones" defect
        (items 3 and 5).

        `CAPTION_WIDGET_TYPES` / `LINKABLE_WIDGET_TYPES` gate them by what the
        RENDERER actually draws, so a type that ignores a caption cannot be offered
        one — an inspector field that does nothing is the same class of lie as a
        caption that cannot be changed.
      */}
      {CAPTION_WIDGET_TYPES.has( definition.type ) ? (
        <div>
          <Text className={classes.fieldLabel}>
            {t( 'widget_inspector_readmodel_caption_label', 'Texto debajo del número' )}
          </Text>
          <TextInput
            size="xs"
            data-testid={componentsTestIds.reportsInspectorCaption}
            value={( definition.widgetComponentProps?.caption as string ) ?? ''}
            placeholder={t( 'widget_inspector_readmodel_caption_placeholder', 'Ej: publicaciones activas' )}
            onChange={( event ) => {
              const next = event.currentTarget.value;
              updateComponentProp( 'caption', next.length ? next : undefined );
            }}
          />
          <Text className={classes.hint} mt={5}>
            {t(
              'widget_inspector_readmodel_caption_hint',
              'Opcional. Aclara qué está contando la tarjeta; el título ya dice de qué se trata. No se muestra cuando la tarjeta está vacía.'
            )}
          </Text>
        </div>
      ) : null}

      {LINKABLE_WIDGET_TYPES.has( definition.type ) ? (
        <div>
          <Text className={classes.fieldLabel}>
            {t( 'widget_inspector_readmodel_href_label', 'Enlace (opcional)' )}
          </Text>
          <TextInput
            size="xs"
            data-testid={componentsTestIds.reportsInspectorHref}
            value={( definition.widgetComponentProps?.href as string ) ?? ''}
            placeholder={t( 'widget_inspector_readmodel_href_placeholder', '/publicaciones?estado=pausada' )}
            onChange={( event ) => {
              const next = event.currentTarget.value;
              updateComponentProp( 'href', next.length ? next : undefined );
            }}
          />
          <Text className={classes.hint} mt={5}>
            {t(
              'widget_inspector_readmodel_href_hint',
              'Una ruta interna de la app. Los informes se comparten, así que la tarjeta descarta cualquier destino que no sea seguro y muestra el número sin enlace.'
            )}
          </Text>
        </div>
      ) : null}
    </Stack>
  );
}
